import { flow, types } from 'mobx-state-tree';
import axios from 'axios';
import { notification } from 'antd';
import { api } from '../constants/api';

const AuthStore = types
  .model('AuthStore', {
    pending: true,
    me: types.maybeNull(
      types.model('MeModel', {
        id: types.maybeNull(types.union(types.string, types.number)),
        firstName: types.maybeNull(types.string),
        lastName: types.maybeNull(types.string),
        fullName: types.maybeNull(types.string),
        email: types.maybeNull(types.string),
        avatar: types.maybeNull(types.string),
        phone: types.maybeNull(types.string),
        areaCode: types.maybeNull(types.string),
        address1: types.maybeNull(types.string),
        address2: types.maybeNull(types.string), 
        zip: types.maybeNull(types.string),
        city: types.maybeNull(types.string),
        state: types.maybeNull(types.string),
        country: types.maybeNull(types.string),
        emailFrequency: types.maybeNull(types.string),
        newsUpdates: types.maybeNull(types.string),
        role: types.optional(types.string, 'user'),
        permissions: types.optional(types.array(types.string), []),
        isApproved: types.optional(types.boolean, false),
      })
    ),
  })
  .views((self) => ({
    get isAdmin() {
      return self.me?.role === 'admin';
    },
    get isLoggedIn() {
      return !!self.me;
    },
    hasPermission(permission) {
      // Admins have all permissions
      if (self.me?.role === 'admin') return true;
      // Check if user has specific permission
      return self.me?.permissions?.includes(permission) || false;
    },
    canAccessPage(page) {
      const pagePermissionMap = {
        'dashboard': 'dashboard',
        'events': 'event_management',
        'member-queue': 'patient_queue',
        'users': 'user_management',
        'screenings': 'screening_data',
        'all-members': 'screening_data',
        'case-management': 'case_management',
        'analytics': 'analytics',
        'audit-logs': 'audit_logs',
        'call-logs': 'voice_calls',
        'ondemand': 'ondemand_management',
        'form-config': 'user_management',
        'data-import': 'user_management'
      };
      return self.hasPermission(pagePermissionMap[page]);
    },
  }))
  .actions((self) => ({
    setToken: (token) => {
      // Token is now stored in an HttpOnly cookie set by the server.
      // Keep localStorage as a flag for quick "am I logged in?" checks only.
      if (token) {
        localStorage.setItem('bhaAuthToken', '1');
      }
    },
    setMe: (userData) => {
      self.me = {
        id: userData.id || userData._id || null,
        firstName: userData.firstName || null,
        lastName: userData.lastName || null,
        fullName: userData.fullName || null,
        email: userData.email || null,
        avatar: userData.avatar || null,
        phone: userData.phone || null,
        areaCode: userData.areaCode || null,
        address1: userData.address1 || null, 
        address2: userData.address2 || null,
        zip: userData.zip || null,
        city: userData.city || null,
        state: userData.state || null,
        country: userData.country || null,
        emailFrequency: userData.emailFrequency || 'never',
        newsUpdates: userData.newsUpdates || 'yes',
        role: userData.role || 'user',
        permissions: userData.permissions || [],
        isApproved: userData.isApproved || false,
      };
    },
    fetchUserProfile: flow(function* () {
      self.pending = true;
      try {
        const response = yield axios.get(api.auth.getProfile);
        self.setMe(response.data);
      } catch (error) {
        console.error('Error fetching user profile:', error);
        notification.error({
          message: 'Failed to fetch user profile',
          description: error.response?.data?.message || 'An error occurred',
        });
      } finally {
        self.pending = false;
      }
    }),
    updateUserProfile: flow(function* (profileData) {
      try {
        const response = yield axios.post(api.auth.updateProfile, profileData);
        self.setMe(response.data);
        notification.success({
          message: 'Profile updated successfully',
        });
      } catch (error) {
        console.error('Error updating user profile:', error);
        notification.error({
          message: 'Failed to update profile',
          description: error.response?.data?.message || 'An error occurred',
        });
      }
    }),
    onCreateUser: flow(function* (values) {
      try {
        const result = yield axios.post(api.auth.signUp, values);
        self.setToken(result.data.token);
        self.setMe(result.data.profile);
        return result.data;
      } catch (error) {
        notification.error({
          message: error.response?.data?.message || 'Something went wrong',
        });
        throw error;
      }
    }),
    onLogin: flow(function* (values) {
      try {
        const result = yield axios.post(api.auth.signIn, values);
        
        self.setToken(result?.data?.token);
        self.setMe(result.data.profile);
        
        return result.data;
      } catch (error) {
        console.error('❌ AuthStore: Login failed:', error);
        notification.error({
          message: error.response?.data?.message || 'Something went wrong',
        });
        throw error;
      }
    }),
    onClientLogin: flow(function* (values) {
      try {
        const result = yield axios.post(api.auth.clientSignIn, values);
        self.setToken(result?.data?.token);
        self.setMe(result.data.client);
        return result.data;
      } catch (error) {
        notification.error({
          message: error.response?.data?.message || 'Something went wrong',
        });
        throw error;
      }
    }),
    onAgentLogin: flow(function* (values) {
      try {
        const result = yield axios.post(api.auth.agentSignIn, values);
        self.setToken(result?.data?.token);
        self.setMe(result.data.profile);
        return result.data;
      } catch (error) {
        notification.error({
          message: error.response?.data?.message || 'Something went wrong',
        });
        throw error;
      }
    }),
    logout: flow(function* () {
      try {
        yield axios.post(api.auth.logout);
      } catch (_) {
        // Best-effort; cookie may already be expired
      }
      self.me = null;
      // Only remove auth flag — don't wipe all localStorage (preserves user prefs, etc.)
      localStorage.removeItem('bhaAuthToken');
    }),
    whoami: flow(function* () {
      try {
        const result = yield axios.get(api.auth.whoami);
        self.setMe(result.data?.profile);
      } catch (error) {
        // If the token/cookie is expired or invalid, clean up auth state
        if (error.response?.status === 401) {
          self.me = null;
          localStorage.removeItem('bhaAuthToken');
        }
        console.error('Error fetching user profile:', error);
      } finally {
        self.pending = false;
      }
    }),
    setAvatar: (avatar) => {
      if (self.me) {
        self.me.avatar = avatar;
      }
    },
    onUpdateProfile: flow(function* (values) {
      try {
        const { data } = yield axios.post(api.user.updateProfile, values);
        if (self.me) {
          self.me.phone = data?.phone;
          self.me.avatar = data?.avatar;
        }
      } catch (error) {
        console.error('Error updating profile:', error);
      }
    }),
    saveInvestmentPreferences: flow(function* (preferences) {
      try {
        const response = yield axios.post(api.investorProfile.saveInvestmentPreferences, preferences);
        return response.data;
      } catch (error) {
        notification.error({
          message: error.response?.data?.message || 'Error saving investment preferences',
        });
        throw error;
      }
    }),
    saveLocationPreferences: flow(function* (preferences) {
      try {
        const response = yield axios.post(api.investorProfile.saveLocationPreferences, preferences);
        return response.data;
      } catch (error) {
        notification.error({
          message: error.response?.data?.message || 'Error saving location preferences',
        });
        throw error;
      }
    }),
    saveFinancialInformation: flow(function* (financialInfo) {
      try {
        const response = yield axios.post(api.investorProfile.saveFinancialInformation, financialInfo);
        return response.data;
      } catch (error) {
        notification.error({
          message: error.response?.data?.message || 'Error saving financial information',
        });
        throw error;
      }
    }),
    getInvestorDetails: flow(function* () {
      try {
        const response = yield axios.get(api.investorProfile.getProfile);
        return response.data;
      } catch (error) {
        notification.error({
          message: error.response?.data?.message || 'Error fetching investor details',
        });
        throw error;
      }
    }),
    searchPropGPT: flow(function* (query) {
      try {
        const response = yield axios.post(api.listing.propGPTSearch, { query });
        return response.data;
      } catch (error) {
        notification.error({
          message: error.response?.data?.message || 'Error fetching data from PropGPT',
        });
        throw error;
      }
    }),
    onClientSignUp: flow(function* (values) {
      try {
        const result = yield axios.post(api.auth.clientSignUp, values);
        self.setToken(result.data.token);
        self.setMe(result.data.client);
        notification.success({
          message: 'Sign up successful',
          description: 'Welcome to Black Heart Association!',
        });
        return result.data;
      } catch (error) {
        notification.error({
          message: 'Sign up failed',
          description: error.response?.data?.message || 'Something went wrong',
        });
        throw error;
      }
    }),
  }));

export default AuthStore;